import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { TargetTypeLabel } from '../../../components/Curation/Labels';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { IUserSegment, IUserSegmentWebhook, UserSegmentType } from '../../../services/types';
import { RouteNames } from '../../config';
import useStyles from './styles';

interface IProps {
  data?: IUserSegmentWebhook[];
  onClickRow: (segmentId: number) => void;
  onUpdate: (data: IUserSegmentWebhook[]) => void;
  onError: (error: Error) => void;
  onDelete: (seg: IUserSegmentWebhook, type: UserSegmentType, idx: number) => void;
  onClickCuration: (segmentItem: IUserSegment, mode: 'create' | 'edit') => void;
  onToggleStatus: (seg: IUserSegmentWebhook, type: UserSegmentType) => void;
}

export const WebhookList = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const history = useHistory();

  if (props.data?.length === 0) {
    return (
      <Paper>
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.noDataBox} flexDirection="column">
          <Typography>No data</Typography>
          <Typography variant="caption">{'Tap the "Create User Segment" at the top to create a first user segment.'}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <div className={classes.root}>
      <Paper>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell>Segment Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">User Count</TableCell>
              <TableCell align="center">Max. Att. Duration</TableCell>
              <TableCell align="center">Persistence</TableCell>
              <TableCell align="center">Reentering</TableCell>
              <TableCell align="center">Curation</TableCell>
              <TableCell align="center"></TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data &&
              props.data.length > 0 &&
              props.data.map((seg, idx) => {
                return (
                  <TableRow
                    component={Link}
                    to={`${RouteNames.UserSegment}/v/${seg.id}`}
                    key={seg.id}
                    hover
                    className={classes.tableRow}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      if (!seg.id) {
                        return;
                      }

                      props.onClickRow(seg.id);
                    }}
                  >
                    <TableCell align="center">{seg.id}</TableCell>
                    <TableCell>
                      {seg.webhookOption && seg.webhookOption.isGreedy && (
                        <Chip size="small" label="Greedy" color="secondary" style={{ marginRight: 8 }} />
                      )}
                      {seg.name}
                    </TableCell>
                    <TableCell>{seg.description}</TableCell>
                    <TableCell align="center">{seg.userCount?.toLocaleString() || 0}</TableCell>
                    <TableCell align="center">{seg.daysToExpiration}</TableCell>

                    {seg.webhookOption && <TableCell align="center">{seg.webhookOption.persistentType}</TableCell>}
                    {seg.webhookOption && <TableCell align="center">{seg.webhookOption.isReenterable ? 'Y' : 'N'}</TableCell>}
                    <TableCell align="center">
                      {(seg.featuredCardNum || 0) + (seg.collectionNum || 0) === 0 ? (
                        <Button
                          // href={`${RouteNames.UserSegment}/v/${seg.id}`}
                          variant="outlined"
                          color="primary"
                          className={classes.segButton}
                          onClick={e => {
                            props.onClickCuration(seg, 'create');
                            history.push(`${RouteNames.UserSegment}/v/${seg.id}/curation/create`);
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          Create
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="default"
                          className={classes.segButton}
                          onClick={e => {
                            props.onClickCuration(seg, 'edit');
                            history.push(`${RouteNames.UserSegment}/v/${seg.id}/curation`);
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      <Box className={classes.abtestCountBox}>
                        <Box className={classes.abtestCountBoxLeft}>Featured Card: {seg.featuredCardNum}</Box>
                        <Box className={classes.abtestCountBoxRight}>{seg.isFeaturedCardsOnTest && <TargetTypeLabel type={'A/B'} />}</Box>
                      </Box>
                      <Box className={classes.abtestCountBox}>
                        <Box className={classes.abtestCountBoxLeft}>Collection: {seg.collectionNum}</Box>
                        <Box className={classes.abtestCountBoxRight}>{seg.isCollectionsOnTest && <TargetTypeLabel type={'A/B'} />}</Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {seg.webhookOption && (
                        <Tooltip title={seg.webhookOption.isActive ? 'Activated' : 'Deactivated'} placement="bottom">
                          <Switch
                            color="primary"
                            checked={seg.webhookOption.isActive}
                            onClick={e => e.stopPropagation()}
                            onChange={e => {
                              props.onToggleStatus(seg, UserSegmentType.Webhook);
                            }}
                            value={idx}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {seg.webhookOption && (
                        <Tooltip title={seg.webhookOption.isActive ? 'You cannot delete it in the active state.' : ''} placement="left-end">
                          <IconButton
                            style={{
                              opacity: seg.webhookOption.isActive ? 0.25 : 1,
                            }}
                            color={seg.webhookOption.isActive ? 'default' : 'primary'}
                            disableFocusRipple={seg.webhookOption.isActive}
                            disableTouchRipple={seg.webhookOption.isActive}
                            disableRipple={seg.webhookOption.isActive}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              props.onDelete(seg, UserSegmentType.Webhook, idx);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};
